import { useState, useEffect } from "react";
import { useAuth } from "../../context/AuthContext";
import DomainsSection from "./DomainsSection";
import ExportCard from "./ExportCard";
import DangerZone from "./DangerZone";
import styles from "./ManageTab.module.css";
import PolicyImagesSection from "./PolicyImagesSection";

export default function AdminTab() {
  const { apiFetch } = useAuth();

  // Invite
  const [instrEmail, setInstrEmail] = useState("");
  const [instrFirst, setInstrFirst] = useState("");
  const [instrLast, setInstrLast] = useState("");
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState("");
  const [error, setError] = useState("");
  const [codes, setCodes] = useState([]);
  const [resending, setResending] = useState(null);

  // Overview data
  const [instructors, setInstructors] = useState([]);
  const [courses, setCourses] = useState([]);
  const [storage, setStorage] = useState(null);
  const [live, setLive] = useState([]);
  const [busy, setBusy] = useState(false);
  const [showTables, setShowTables] = useState(false);
  const [showArchived, setShowArchived] = useState(false);

  // Semesters
  const [semesters, setSemesters] = useState([]);
  const [semTerm, setSemTerm] = useState("Fall");
  const [semYear, setSemYear] = useState(new Date().getFullYear());
  const [semBusy, setSemBusy] = useState(false);

  // Email composer
  const [selected, setSelected] = useState([]);
  const [subject, setSubject] = useState("");
  const [body, setBody] = useState("");
  const [mailResult, setMailResult] = useState(null);
  const [mailBusy, setMailBusy] = useState(false);

  // Admin sender
  const [sender, setSender] = useState({
    host: "",
    port: 587,
    username: "",
    from_email: "",
    from_name: "Gradiance",
    use_tls: true,
  });
  const [senderPass, setSenderPass] = useState("");
  const [senderConfigured, setSenderConfigured] = useState(false);
  const [senderResult, setSenderResult] = useState(null);
  const [senderBusy, setSenderBusy] = useState(false);

  const flash = (msg, isError = false) => {
    if (isError) {
      setError(msg);
      setSuccess("");
    } else {
      setSuccess(msg);
      setError("");
    }
    setTimeout(() => {
      setSuccess("");
      setError("");
    }, 5000);
  };

  useEffect(() => {
    loadOverview();
    loadCodes();
    loadSender();
    loadSemesters();
  }, []); // eslint-disable-line

  const loadSemesters = async () => {
    try {
      const res = await apiFetch("/api/admin/semesters");
      if (res.ok) setSemesters(await res.json());
      // Pre-fill the create form with the current term/year suggestion.
      const sug = await apiFetch("/api/admin/semesters/suggest");
      if (sug.ok) {
        const d = await sug.json();
        setSemTerm(d.term);
        setSemYear(d.year);
      }
    } catch {
      /* leave defaults */
    }
  };

  const createSemester = async () => {
    setSemBusy(true);
    setError("");
    setSuccess("");
    try {
      const res = await apiFetch("/api/admin/semesters", {
        method: "POST",
        body: JSON.stringify({ term: semTerm, year: Number(semYear) }),
      });
      const d = await res.json().catch(() => ({}));
      if (res.ok) {
        setSuccess(d.message);
        setTimeout(() => setSuccess(""), 4000);
        loadSemesters();
      } else setError(d.detail || "Could not create semester.");
    } catch (err) {
      setError(err.message);
    } finally {
      setSemBusy(false);
    }
  };

  const toggleSemesterActive = async (sem) => {
    try {
      const res = await apiFetch(`/api/admin/semesters/${sem.id}/active`, {
        method: "PUT",
        body: JSON.stringify({ is_active: !sem.is_active }),
      });
      if (res.ok) loadSemesters();
    } catch {
      /* ignore */
    }
  };

  const loadOverview = async () => {
    setBusy(true);
    try {
      const res = await apiFetch("/api/admin/overview");
      if (res.ok) {
        const d = await res.json();
        setInstructors(d.instructors || []);
        setCourses(d.courses || []);
        setStorage(d.storage || null);
        setLive(d.live || []);
      }
    } catch {
      /* ignore */
    } finally {
      setBusy(false);
    }
  };

  const loadCodes = async () => {
    try {
      const res = await apiFetch("/api/admin/invite-codes");
      if (res.ok) setCodes(await res.json());
    } catch {
      /* ignore */
    }
  };

  const loadSender = async () => {
    try {
      const res = await apiFetch("/api/admin/email-settings");
      if (res.ok) {
        const d = await res.json();
        setSender({
          host: d.host || "",
          port: d.port || 587,
          username: d.username || "",
          from_email: d.from_email || "",
          from_name: d.from_name || "Gradiance",
          use_tls: d.use_tls !== false,
        });
        setSenderConfigured(!!d.is_configured);
      }
    } catch {
      /* ignore */
    }
  };

  const handleGenerateCode = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const res = await apiFetch("/api/auth/invite-codes/instructor", {
        method: "POST",
        body: JSON.stringify({
          email: instrEmail.trim().toLowerCase(),
          first_name: instrFirst.trim(),
          last_name: instrLast.trim(),
        }),
      });
      const data = await res.json();
      if (res.ok) {
        flash(`Invite sent to ${instrEmail}`);
        setInstrEmail("");
        setInstrFirst("");
        setInstrLast("");
        await loadCodes();
        await loadOverview();
      } else {
        flash(data.detail || "Failed to generate invite.", true);
      }
    } catch (err) {
      flash(err.message, true);
    } finally {
      setLoading(false);
    }
  };

  const handleResendInvite = async (code) => {
    setResending(code);
    try {
      const res = await apiFetch(`/api/admin/invite-codes/${code}/resend`, {
        method: "POST",
      });
      const data = await res.json().catch(() => ({}));
      if (res.ok) {
        flash(data.message || "Invite re-sent.");
      } else {
        flash(data.detail || "Could not re-send that invite.", true);
      }
    } catch (err) {
      flash(err.message, true);
    } finally {
      setResending(null);
    }
  };

  const toggleRecipient = (id) => {
    setSelected((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id],
    );
  };

  const sendMail = async () => {
    setMailBusy(true);
    setMailResult(null);
    try {
      const res = await apiFetch("/api/admin/email", {
        method: "POST",
        body: JSON.stringify({ recipient_ids: selected, subject, body }),
      });
      const d = await res.json().catch(() => ({}));
      if (res.ok) {
        setMailResult({ ok: true, text: d.message });
        setSubject("");
        setBody("");
        setSelected([]);
      } else {
        setMailResult({ ok: false, text: d.detail || "Send failed." });
      }
    } catch (err) {
      setMailResult({ ok: false, text: err.message });
    } finally {
      setMailBusy(false);
    }
  };

  const saveSender = async () => {
    setSenderBusy(true);
    setSenderResult(null);
    try {
      const payload = { ...sender };
      if (senderPass) payload.password = senderPass;
      const res = await apiFetch("/api/admin/email-settings", {
        method: "PUT",
        body: JSON.stringify(payload),
      });
      const d = await res.json().catch(() => ({}));
      if (res.ok) {
        setSenderResult({ ok: true, text: "Saved. Send a test to confirm." });
        setSenderPass("");
        setSenderConfigured(true);
      } else {
        setSenderResult({ ok: false, text: d.detail || "Could not save." });
      }
    } catch (err) {
      setSenderResult({ ok: false, text: err.message });
    } finally {
      setSenderBusy(false);
    }
  };

  const testSender = async () => {
    setSenderBusy(true);
    setSenderResult(null);
    try {
      const res = await apiFetch("/api/admin/email-settings/test", {
        method: "POST",
      });
      const d = await res.json().catch(() => ({}));
      setSenderResult(
        res.ok
          ? {
              ok: true,
              text: `${d.message}${d.personal ? "" : " (system account)"}`,
            }
          : { ok: false, text: d.detail || "Test failed." },
      );
    } catch (err) {
      setSenderResult({ ok: false, text: err.message });
    } finally {
      setSenderBusy(false);
    }
  };

  const clearSender = async () => {
    if (
      !window.confirm(
        "Remove the Gradiance sender and fall back to the system email?",
      )
    )
      return;
    setSenderBusy(true);
    setSenderResult(null);
    try {
      const res = await apiFetch("/api/admin/email-settings", {
        method: "DELETE",
      });
      if (res.ok) {
        setSender({
          host: "",
          port: 587,
          username: "",
          from_email: "",
          from_name: "Gradiance",
          use_tls: true,
        });
        setSenderPass("");
        setSenderConfigured(false);
        setSenderResult({ ok: true, text: "Reverted to the system sender." });
      }
    } catch (err) {
      setSenderResult({ ok: false, text: err.message });
    } finally {
      setSenderBusy(false);
    }
  };

  const visibleCourses = showArchived
    ? courses
    : courses.filter((c) => c.is_active);
  const pill = (text, color) => (
    <span
      style={{
        fontSize: 10,
        padding: "2px 6px",
        borderRadius: 4,
        marginLeft: 6,
        background: `${color}22`,
        color,
        border: `1px solid ${color}55`,
      }}
    >
      {text}
    </span>
  );

  return (
    <div className={styles.panel}>
      {success && <div className={styles.success}>{success}</div>}
      {error && <div className={styles.error}>{error}</div>}

      {/* ── Live Operations ── */}
      <section className={styles.section}>
        <h3 className={styles.sectionTitle}>Live Now</h3>
        {busy && live.length === 0 ? (
          <p style={{ fontSize: 13, color: "var(--txt-dim)" }}>Loading…</p>
        ) : live.length === 0 ? (
          <p className={styles.empty}>No queue sessions are open right now.</p>
        ) : (
          <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
            {live.map((s) => (
              <div
                key={s.session_id}
                style={{
                  fontSize: 13,
                  padding: "10px 12px",
                  borderRadius: 6,
                  background: "var(--bg-card)",
                  border: "1px solid var(--border)",
                  display: "flex",
                  justifyContent: "space-between",
                  gap: 10,
                }}
              >
                <span style={{ color: "var(--txt)" }}>
                  {s.instructor}
                  {s.is_paused && pill("paused", "#f5a524")}
                </span>
                <span
                  style={{ color: "var(--txt-faint)", whiteSpace: "nowrap" }}
                >
                  {s.waiting} waiting · {s.policy} · {s.session_date}
                </span>
              </div>
            ))}
          </div>
        )}
        <button
          className="btn-ghost"
          onClick={loadOverview}
          disabled={busy}
          style={{ marginTop: 10, fontSize: 12 }}
        >
          ↻ Refresh
        </button>
      </section>

      <div className={styles.divider} />

      {/* ── Semesters ── */}
      <section className={styles.section}>
        <h3 className={styles.sectionTitle}>Semesters</h3>
        <p
          style={{
            fontSize: 12,
            color: "var(--txt-dim)",
            marginBottom: 10,
            lineHeight: 1.5,
          }}
        >
          Terms are institution-wide. Instructors pick one when creating a
          course, and analytics can be filtered by semester. Each term is unique
          — "Fall 2026" can only exist once.
        </p>

        <div
          style={{
            display: "flex",
            gap: 8,
            alignItems: "flex-end",
            flexWrap: "wrap",
            marginBottom: 14,
          }}
        >
          <div>
            <label
              style={{
                fontSize: 11,
                color: "var(--txt-faint)",
                display: "block",
                marginBottom: 3,
              }}
            >
              Term
            </label>
            <select
              value={semTerm}
              onChange={(e) => setSemTerm(e.target.value)}
            >
              <option value="Fall">Fall</option>
              <option value="Spring">Spring</option>
              <option value="Summer">Summer</option>
            </select>
          </div>
          <div>
            <label
              style={{
                fontSize: 11,
                color: "var(--txt-faint)",
                display: "block",
                marginBottom: 3,
              }}
            >
              Year
            </label>
            <input
              type="number"
              min="2000"
              max="2100"
              value={semYear}
              onChange={(e) => setSemYear(e.target.value)}
              style={{ width: 90 }}
            />
          </div>
          <button
            className="btn-primary"
            onClick={createSemester}
            disabled={semBusy}
            style={{ fontSize: 13 }}
          >
            Create {semTerm} {semYear}
          </button>
        </div>

        {semesters.length === 0 ? (
          <p className={styles.empty}>
            No semesters yet. Create one so instructors can add courses.
          </p>
        ) : (
          <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
            {semesters.map((s) => (
              <div
                key={s.id}
                style={{
                  fontSize: 13,
                  padding: "9px 12px",
                  borderRadius: 6,
                  background: "var(--bg-card)",
                  border: "1px solid var(--border)",
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  gap: 10,
                  opacity: s.is_active ? 1 : 0.55,
                }}
              >
                <span style={{ color: "var(--txt)", fontWeight: 600 }}>
                  {s.name}
                  {!s.is_active && (
                    <span
                      style={{
                        color: "var(--txt-faint)",
                        fontWeight: 400,
                        fontSize: 11,
                      }}
                    >
                      {" "}
                      · inactive
                    </span>
                  )}
                </span>
                <span
                  style={{ display: "flex", alignItems: "center", gap: 12 }}
                >
                  <span style={{ color: "var(--txt-faint)", fontSize: 12 }}>
                    {s.course_count} course{s.course_count !== 1 ? "s" : ""}
                  </span>
                  <button
                    className="btn-ghost"
                    onClick={() => toggleSemesterActive(s)}
                    style={{ fontSize: 11, padding: "3px 8px" }}
                  >
                    {s.is_active ? "Deactivate" : "Activate"}
                  </button>
                </span>
              </div>
            ))}
          </div>
        )}
      </section>

      <div className={styles.divider} />

      {/* ── Instructors ── */}
      <section className={styles.section}>
        <h3 className={styles.sectionTitle}>Instructors</h3>
        <p style={{ fontSize: 12, color: "var(--txt-dim)", marginBottom: 12 }}>
          {instructors.length} account{instructors.length !== 1 ? "s" : ""}.
          Tick anyone you'd like to email below.
        </p>
        {instructors.length === 0 ? (
          <p className={styles.empty}>No instructor accounts yet.</p>
        ) : (
          <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
            {instructors.map((u) => (
              <label
                key={u.id}
                style={{
                  display: "flex",
                  alignItems: "flex-start",
                  gap: 10,
                  fontSize: 13,
                  padding: "10px 12px",
                  borderRadius: 6,
                  background: "var(--bg-card)",
                  border: "1px solid var(--border)",
                  cursor: "pointer",
                }}
              >
                <input
                  type="checkbox"
                  checked={selected.includes(u.id)}
                  onChange={() => toggleRecipient(u.id)}
                  style={{
                    width: 16,
                    height: 16,
                    flexShrink: 0,
                    margin: 0,
                    marginTop: 3,
                  }}
                />
                <span style={{ flex: 1 }}>
                  <span style={{ color: "var(--txt)", fontWeight: 600 }}>
                    {u.legal_name || u.display_name}
                  </span>
                  {u.role !== "INSTRUCTOR" && pill(u.role, "#60a5fa")}
                  {!u.is_active && pill("inactive", "#e5484d")}
                  {!u.password_set && pill("password not set", "#f5a524")}
                  {u.session_open && pill("queue open", "#30a46c")}
                  {u.smtp_configured && pill("own SMTP", "#a78bfa")}
                  <br />
                  <span style={{ fontSize: 11, color: "var(--txt-faint)" }}>
                    {u.email}
                    {u.courses.length > 0 && ` · ${u.courses.join(", ")}`}
                    {u.archived_courses > 0 &&
                      ` · ${u.archived_courses} archived`}
                    {u.courses.length === 0 &&
                      u.archived_courses === 0 &&
                      " · no courses"}
                  </span>
                </span>
              </label>
            ))}
          </div>
        )}
      </section>

      <div className={styles.divider} />

      {/* ── Email Instructors ── */}
      <section className={styles.section}>
        <h3 className={styles.sectionTitle}>Email Instructors</h3>
        <p style={{ fontSize: 12, color: "var(--txt-dim)", marginBottom: 12 }}>
          {selected.length === 0
            ? "Select recipients above to send a message."
            : `${selected.length} recipient${selected.length !== 1 ? "s" : ""} selected.`}{" "}
          Each message is sent individually, so nobody sees anyone else's
          address.
        </p>
        <div className={styles.field}>
          <label>Subject</label>
          <input
            value={subject}
            onChange={(e) => setSubject(e.target.value)}
            placeholder="Gradiance update"
          />
        </div>
        <div className={styles.field}>
          <label>Message</label>
          <textarea
            rows={6}
            value={body}
            onChange={(e) => setBody(e.target.value)}
            placeholder="Write your message…"
            style={{
              background: "var(--bg)",
              border: "1px solid var(--border)",
              borderRadius: 6,
              color: "var(--txt)",
              fontSize: 13,
              padding: "8px 10px",
              fontFamily: "inherit",
              resize: "vertical",
            }}
          />
        </div>
        <button
          className="btn-primary"
          onClick={sendMail}
          disabled={
            mailBusy || selected.length === 0 || !subject.trim() || !body.trim()
          }
        >
          {mailBusy ? "Sending…" : `Send to ${selected.length || 0}`}
        </button>
        {mailResult && (
          <div
            className={mailResult.ok ? styles.success : styles.error}
            style={{ marginTop: 12 }}
          >
            {mailResult.ok ? "✓ " : "⚠ "}
            {mailResult.text}
          </div>
        )}
      </section>

      <div className={styles.divider} />

      {/* ── Gradiance Sender ── */}
      <section className={styles.section}>
        <h3 className={styles.sectionTitle}>Gradiance Email Sender</h3>
        <p style={{ fontSize: 12, color: "var(--txt-dim)", marginBottom: 6 }}>
          The address admin mail is sent from — separate from the account you
          use for student email in your instructor role.
        </p>
        <div
          style={{ fontSize: 11, color: "var(--txt-faint)", marginBottom: 12 }}
        >
          Status:{" "}
          {senderConfigured ? (
            <strong style={{ color: "var(--green, #30a46c)" }}>
              using the Gradiance sender
            </strong>
          ) : (
            <strong>falling back to the system email</strong>
          )}
        </div>

        <div
          style={{ display: "grid", gridTemplateColumns: "1fr 100px", gap: 10 }}
        >
          <div className={styles.field}>
            <label>SMTP host</label>
            <input
              value={sender.host}
              placeholder="mail.yourhost.com"
              onChange={(e) =>
                setSender((s) => ({ ...s, host: e.target.value }))
              }
            />
          </div>
          <div className={styles.field}>
            <label>Port</label>
            <input
              type="number"
              value={sender.port}
              onChange={(e) =>
                setSender((s) => ({ ...s, port: Number(e.target.value) }))
              }
            />
          </div>
        </div>
        <div className={styles.field}>
          <label>Username</label>
          <input
            value={sender.username}
            placeholder="noreply@gradiance.org"
            onChange={(e) =>
              setSender((s) => ({ ...s, username: e.target.value }))
            }
          />
        </div>
        <div className={styles.field}>
          <label>
            Password{" "}
            {senderConfigured && (
              <span style={{ color: "var(--txt-faint)" }}>
                (blank keeps current)
              </span>
            )}
          </label>
          <input
            type="password"
            value={senderPass}
            autoComplete="new-password"
            placeholder={senderConfigured ? "••••••••" : "Mailbox password"}
            onChange={(e) => setSenderPass(e.target.value)}
          />
        </div>
        <div
          style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}
        >
          <div className={styles.field}>
            <label>From name</label>
            <input
              value={sender.from_name}
              onChange={(e) =>
                setSender((s) => ({ ...s, from_name: e.target.value }))
              }
            />
          </div>
          <div className={styles.field}>
            <label>
              From email{" "}
              <span style={{ color: "var(--txt-faint)" }}>(optional)</span>
            </label>
            <input
              value={sender.from_email}
              placeholder="defaults to username"
              onChange={(e) =>
                setSender((s) => ({ ...s, from_email: e.target.value }))
              }
            />
          </div>
        </div>
        <label
          style={{
            display: "flex",
            alignItems: "center",
            gap: 8,
            fontSize: 13,
            margin: "8px 0 14px",
          }}
        >
          <input
            type="checkbox"
            style={{ width: 16, height: 16, flexShrink: 0, margin: 0 }}
            checked={sender.use_tls}
            onChange={(e) =>
              setSender((s) => ({ ...s, use_tls: e.target.checked }))
            }
          />
          Use STARTTLS (recommended)
        </label>

        <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
          <button
            className="btn-primary"
            onClick={saveSender}
            disabled={senderBusy}
          >
            Save
          </button>
          <button
            className="btn-ghost"
            onClick={testSender}
            disabled={senderBusy}
          >
            Send Test
          </button>
          {senderConfigured && (
            <button
              className="btn-ghost"
              onClick={clearSender}
              disabled={senderBusy}
              style={{ color: "var(--red, #e5484d)" }}
            >
              Remove
            </button>
          )}
        </div>
        {senderResult && (
          <div
            className={senderResult.ok ? styles.success : styles.error}
            style={{ marginTop: 12 }}
          >
            {senderResult.ok ? "✓ " : "⚠ "}
            {senderResult.text}
          </div>
        )}
      </section>

      <div className={styles.divider} />

      {/* ── Courses & Storage ── */}
      <section className={styles.section}>
        <h3 className={styles.sectionTitle}>Courses &amp; Storage</h3>
        <p style={{ fontSize: 12, color: "var(--txt-dim)", marginBottom: 6 }}>
          Gradiance stores no files per course — everything is database rows, so
          per-course sizes are estimates from row counts.
        </p>
        {storage && (
          <div
            style={{
              fontSize: 12,
              color: "var(--txt-faint)",
              marginBottom: 12,
            }}
          >
            Database total{" "}
            <strong style={{ color: "var(--txt)" }}>
              {storage.total_size}
            </strong>
            {" · "}
            {storage.total_users} users · {storage.total_sessions} sessions
            {" · "}
            {storage.total_entries} queue entries
            <button
              className="btn-ghost"
              onClick={() => setShowTables((t) => !t)}
              style={{ fontSize: 11, padding: "2px 6px", marginLeft: 8 }}
            >
              {showTables ? "hide tables" : "by table"}
            </button>
          </div>
        )}

        {showTables && storage && (
          <div
            style={{
              marginBottom: 14,
              display: "flex",
              flexDirection: "column",
              gap: 4,
            }}
          >
            {storage.tables.map((t) => (
              <div
                key={t.table}
                style={{
                  fontSize: 11,
                  display: "flex",
                  justifyContent: "space-between",
                  color: "var(--txt-faint)",
                  padding: "3px 0",
                  borderBottom: "1px solid var(--border)",
                }}
              >
                <span style={{ fontFamily: "var(--font-mono, monospace)" }}>
                  {t.table}
                </span>
                <span>
                  {t.rows.toLocaleString()} rows · {t.size}
                </span>
              </div>
            ))}
          </div>
        )}

        <label
          style={{
            display: "flex",
            alignItems: "center",
            gap: 8,
            fontSize: 12,
            marginBottom: 10,
          }}
        >
          <input
            type="checkbox"
            style={{ width: 16, height: 16, flexShrink: 0, margin: 0 }}
            checked={showArchived}
            onChange={(e) => setShowArchived(e.target.checked)}
          />
          Include archived courses
        </label>

        {visibleCourses.length === 0 ? (
          <p className={styles.empty}>No courses.</p>
        ) : (
          <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
            {visibleCourses.map((c) => (
              <div
                key={c.id}
                style={{
                  fontSize: 13,
                  padding: "10px 12px",
                  borderRadius: 6,
                  background: "var(--bg-card)",
                  border: "1px solid var(--border)",
                  opacity: c.is_active ? 1 : 0.65,
                }}
              >
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    gap: 10,
                  }}
                >
                  <span style={{ color: "var(--txt)", fontWeight: 600 }}>
                    {c.course_code}
                    {!c.is_active && pill("archived", "#8b8b8b")}
                  </span>
                  <span
                    style={{ color: "var(--txt-faint)", whiteSpace: "nowrap" }}
                  >
                    ~{c.estimated_size}
                  </span>
                </div>
                <div
                  style={{
                    fontSize: 11,
                    color: "var(--txt-faint)",
                    marginTop: 3,
                  }}
                >
                  {c.course_name} · {c.instructor}
                </div>
                <div
                  style={{
                    fontSize: 11,
                    color: "var(--txt-faint)",
                    marginTop: 3,
                  }}
                >
                  {c.counts.enrollments} enrolled · {c.counts.competencies}{" "}
                  competencies
                  {" · "}
                  {c.counts.queue_entries} queue entries
                </div>
              </div>
            ))}
          </div>
        )}
      </section>

      <div className={styles.divider} />

      {/* ── Invite Instructor ── */}
      <section className={styles.section}>
        <h3 className={styles.sectionTitle}>Invite Instructor</h3>
        <p style={{ fontSize: 12, color: "var(--txt-dim)", marginBottom: 12 }}>
          An invite code will be emailed to the instructor with a registration
          link.
        </p>
        <form onSubmit={handleGenerateCode} className={styles.form}>
          <div className={styles.row}>
            <div className={styles.field}>
              <label>First Name</label>
              <input
                placeholder="Jane"
                value={instrFirst}
                onChange={(e) => setInstrFirst(e.target.value)}
                required
              />
            </div>
            <div className={styles.field}>
              <label>Last Name</label>
              <input
                placeholder="Doe"
                value={instrLast}
                onChange={(e) => setInstrLast(e.target.value)}
                required
              />
            </div>
          </div>
          <div className={styles.field}>
            <label>School Email</label>
            <input
              type="email"
              placeholder="instructor@uwec.edu"
              value={instrEmail}
              onChange={(e) => setInstrEmail(e.target.value)}
              required
            />
          </div>
          <button type="submit" className="btn-primary" disabled={loading}>
            {loading ? "Sending…" : "Generate & Send Invite"}
          </button>
        </form>
      </section>

      {codes.length > 0 && (
        <>
          <div className={styles.divider} />
          <section className={styles.section}>
            <h3 className={styles.sectionTitle}>Invite Codes</h3>
            <div style={{ display: "flex", flexDirection: "column", gap: 5 }}>
              {codes.map((c, i) => (
                <div
                  key={i}
                  style={{
                    fontSize: 12,
                    padding: "8px 10px",
                    borderRadius: 6,
                    background: "var(--bg-card)",
                    border: "1px solid var(--border)",
                    display: "flex",
                    justifyContent: "space-between",
                    gap: 10,
                  }}
                >
                  <span style={{ minWidth: 0 }}>
                    <span style={{ color: "var(--txt)", fontWeight: 600 }}>
                      {c.invited_name ||
                        c.invited_email ||
                        c.redeemed_by ||
                        "Unknown recipient"}
                    </span>
                    {pill(c.role, "#60a5fa")}
                    <br />
                    <span style={{ fontSize: 11, color: "var(--txt-faint)" }}>
                      {c.invited_email || c.redeemed_by || "—"}
                      {" · "}
                      <span
                        style={{ fontFamily: "var(--font-mono, monospace)" }}
                      >
                        {c.code}
                      </span>
                    </span>
                  </span>
                  <span
                    style={{
                      color: "var(--txt-faint)",
                      whiteSpace: "nowrap",
                      display: "flex",
                      alignItems: "center",
                      gap: 8,
                    }}
                  >
                    {c.used_at
                      ? `✓ redeemed${c.redeemed_by && c.redeemed_by !== c.invited_email ? ` as ${c.redeemed_by}` : ""}`
                      : c.is_valid
                        ? "⏳ pending"
                        : "✕ expired"}
                    {/* Only where a resend can actually work: still unredeemed,
                        and an address on the invite to send it to. Re-sends the
                        same code rather than minting a new one, so a link the
                        recipient may still have keeps working. */}
                    {!c.used_at && c.is_valid && c.invited_email && (
                      <button
                        type="button"
                        className="btn-ghost"
                        style={{ fontSize: 11, padding: "2px 8px" }}
                        disabled={resending === c.code}
                        onClick={() => handleResendInvite(c.code)}
                      >
                        {resending === c.code ? "Sending…" : "Resend"}
                      </button>
                    )}
                  </span>
                </div>
              ))}
            </div>
          </section>
        </>
      )}

      {/* Was nested inside {codes.length > 0 && ...}, so it disappeared
          whenever there were no invite codes — unrelated conditions. */}
      <div className={styles.divider} />

      <PolicyImagesSection />

      <div className={styles.divider} />

      <DomainsSection />

      <div className={styles.divider} />

      {/* Export sits immediately above Danger Zone on purpose: the only
          safe order of operations is export, verify, then delete. */}
      <ExportCard courses={courses} admin />

      <div className={styles.divider} />

      <DangerZone
        courses={courses}
        instructors={instructors}
        onChanged={loadOverview}
      />
    </div>
  );
}
